import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import RobustSelect from '../../components/RobustSelect';
import { SelectItem } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Trophy, Medal, Award, TrendingUp, Download, FileSpreadsheet, ChevronDown, ChevronUp, BookOpen, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

function BatchRankingsNew() {
  const location = useLocation();
  const [batches, setBatches] = useState([]);
  const [tests, setTests] = useState([]);
  const [batchTests, setBatchTests] = useState([]); // Tests available in the batch
  const [batchTopics, setBatchTopics] = useState([]); // Topics/Question banks in the batch
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchBatches();
    fetchTests();
    
    // Pre-select batch if passed from navigation
    if (location.state?.batchId) {
      setSelectedBatch(location.state.batchId);
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedBatch) {
      const testFilter = (selectedTest === 'all' || !selectedTest) ? '' : selectedTest;
      fetchRankings(selectedBatch, testFilter);
    }
  }, [selectedBatch, selectedTest]);

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to fetch batches');
    }
  };

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    }
  };

  const fetchRankings = async (batchId, testId = '') => {
    setLoading(true);
    try {
      const url = testId 
        ? `/rankings/batch/${batchId}?test_id=${testId}`
        : `/rankings/batch/${batchId}`;
      const response = await api.get(url);
      
      // Handle both old and new response format
      if (response.data.rankings) {
        setRankings(response.data.rankings);
        setBatchTests(response.data.tests || []);
        setBatchTopics(response.data.topics || []);
      } else {
        // Legacy format - array directly
        setRankings(response.data);
        setBatchTests([]);
        setBatchTopics([]);
      }
    } catch (error) {
      console.error('Error fetching rankings:', error);
      toast.error('Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${base64Data}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    setExporting(true);
    try {
      const testFilter = (selectedTest === 'all' || !selectedTest) ? '' : selectedTest;
      const url = testFilter
        ? `/rankings/batch/${selectedBatch}/export/excel?test_id=${testFilter}`
        : `/rankings/batch/${selectedBatch}/export/excel`;
      
      const response = await api.get(url);
      downloadFile(response.data.data, response.data.filename);
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    setExporting(true);
    try {
      const testFilter = (selectedTest === 'all' || !selectedTest) ? '' : selectedTest;
      const url = testFilter
        ? `/rankings/batch/${selectedBatch}/export/pdf?test_id=${testFilter}`
        : `/rankings/batch/${selectedBatch}/export/pdf`;
      
      const response = await api.get(url);
      downloadFile(response.data.data, response.data.filename);
      toast.success('PDF file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF');
    } finally {
      setExporting(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className='w-6 h-6 text-yellow-500' />;
    if (rank === 2) return <Medal className='w-6 h-6 text-gray-400' />;
    if (rank === 3) return <Award className='w-6 h-6 text-amber-600' />;
    return <span className='text-lg font-bold text-gray-600'>#{rank}</span>;
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (rank === 2) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (rank === 3) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} min`;
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50';
    if (percentage >= 60) return 'text-blue-600 bg-blue-50';
    if (percentage >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getTopicScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-700 bg-green-100 border-green-200';
    if (percentage >= 60) return 'text-blue-700 bg-blue-100 border-blue-200';
    if (percentage >= 40) return 'text-yellow-700 bg-yellow-100 border-yellow-200';
    return 'text-red-700 bg-red-100 border-red-200';
  };

  const getWeakestTopics = (topicPerformance) => {
    if (!topicPerformance || Object.keys(topicPerformance).length === 0) return [];
    
    const topicScores = Object.entries(topicPerformance)
      .map(([topicId, data]) => {
        const topic = batchTopics.find(t => t.id === topicId);
        return {
          id: topicId,
          name: topic?.title || 'Unknown Topic',
          percentage: data.percentage
        };
      })
      .filter(t => t.percentage < 70)
      .sort((a, b) => a.percentage - b.percentage);
    
    return topicScores.slice(0, 3);
  };

  const toggleRowExpand = (userId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  return (
    <Layout>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2' data-testid="batch-rankings-title">Batch Rankings</h1>
            <p className='text-gray-600'>Performance leaderboard with individual test scores</p>
          </div>
          <div className='flex items-center gap-2'>
            <TrendingUp className='w-5 h-5 text-blue-600' />
          </div>
        </div>

        <Card className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Select Batch
              </label>
              <RobustSelect
                value={selectedBatch}
                onValueChange={setSelectedBatch}
                placeholder='Choose a batch'
                testId="batch-selector"
              >
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.name} {batch.year && `(${batch.year})`} - {batch.user_count} users
                  </SelectItem>
                ))}
              </RobustSelect>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Filter by Test (Optional)
              </label>
              <RobustSelect
                value={selectedTest}
                onValueChange={setSelectedTest}
                placeholder='All tests'
                testId="test-filter"
              >
                <SelectItem value="all">All Tests</SelectItem>
                {tests.map((test) => (
                  <SelectItem key={test.id} value={test.id}>
                    {test.title}
                  </SelectItem>
                ))}
              </RobustSelect>
            </div>
          </div>

          {selectedBatch && rankings.length > 0 && (
            <div className='flex gap-2 mt-4 pt-4 border-t'>
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={exporting}
                className="flex items-center gap-2"
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
              <Button
                variant="outline"
                onClick={handleExportPDF}
                disabled={exporting}
                className="flex items-center gap-2"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export to PDF'}
              </Button>
            </div>
          )}
        </Card>

        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='spinner'></div>
          </div>
        ) : rankings.length === 0 ? (
          <Card className='p-12 text-center'>
            <Trophy className='w-16 h-16 mx-auto mb-4 text-gray-400' />
            <h3 className='text-xl font-semibold mb-2'>No Rankings Available</h3>
            <p className='text-gray-600'>
              {selectedBatch ? 'No users in this batch have completed any tests yet.' : 'Select a batch to view rankings.'}
            </p>
          </Card>
        ) : (
          <Card className='overflow-hidden'>
            <div className='bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white'>
              <h2 className='text-2xl font-bold mb-2'>
                {batches.find(b => b.id === selectedBatch)?.name || 'Batch'} Leaderboard
              </h2>
              <p className='text-blue-100'>
                {selectedTest && selectedTest !== 'all'
                  ? `Rankings for: ${tests.find(t => t.id === selectedTest)?.title || 'Selected Test'}`
                  : 'Rankings based on average performance across all tests'
                }
              </p>
              <p className='text-blue-100 text-sm mt-1'>
                Tiebreaker: Students with same score are ranked by fastest completion time
              </p>
            </div>

            {/* Test columns legend */}
            {batchTests.length > 1 && (
              <div className='bg-blue-50 px-6 py-3 border-b'>
                <p className='text-sm font-medium text-blue-800 mb-2'>Test Legend:</p>
                <div className='flex flex-wrap gap-2'>
                  {batchTests.map((test, idx) => (
                    <span key={test.id} className='text-xs bg-white px-2 py-1 rounded border'>
                      <strong>T{idx + 1}:</strong> {test.title.length > 30 ? test.title.substring(0, 30) + '...' : test.title}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className='p-6'>
              {/* Table Header - Always show when there are tests */}
              {batchTests.length > 0 && (
                <div className='hidden lg:block mb-3 overflow-x-auto'>
                  <div className='grid gap-2 px-4 text-xs font-semibold text-gray-600 uppercase min-w-max' 
                       style={{ gridTemplateColumns: `60px 1fr repeat(${batchTests.length}, 60px) 100px 50px` }}>
                    <div className='text-center'>Rank</div>
                    <div>Cadet</div>
                    {batchTests.map((test, idx) => (
                      <div key={test.id} className='text-center' title={test.title}>T{idx + 1}</div>
                    ))}
                    <div className='text-center'>Average</div>
                    <div className='text-center'>Details</div>
                  </div>
                </div>
              )}

              <div className='space-y-3'>
                {rankings.map((student, index) => {
                  const rank = index + 1;
                  const isTopThree = rank <= 3;
                  const isExpanded = expandedRows.has(student.user_id);
                  const testScores = student.test_scores || {};
                  const numTestCols = batchTests.length;
                  
                  return (
                    <div key={student.user_id} data-testid={`ranking-row-${rank}`}>
                      {/* Desktop Layout with Grid */}
                      <div
                        className={`hidden lg:grid gap-2 items-center p-4 rounded-lg border-2 transition-all hover:shadow-md cursor-pointer overflow-x-auto min-w-max ${
                          isTopThree ? 'bg-gradient-to-r from-gray-50 to-white ' + getRankBadgeColor(rank) : 'bg-white border-gray-200'
                        }`}
                        style={{ gridTemplateColumns: `60px 1fr repeat(${numTestCols}, 60px) 100px 50px` }}
                        onClick={() => batchTests.length > 0 && toggleRowExpand(student.user_id)}
                      >
                        {/* Rank */}
                        <div className='flex items-center justify-center'>
                          <div className='w-12 h-12 rounded-full bg-white border-2 flex items-center justify-center'>
                            {getRankIcon(rank)}
                          </div>
                        </div>
                        
                        {/* Cadet Info */}
                        <div className='min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <h3 className='text-lg font-semibold text-gray-900 truncate'>{student.user_name}</h3>
                            {isTopThree && (
                              <Badge className={getRankBadgeColor(rank)}>
                                Rank {rank}
                              </Badge>
                            )}
                          </div>
                          <p className='text-sm text-gray-600 truncate'>{student.user_email}</p>
                        </div>
                        
                        {/* Test Scores - Show ALL tests */}
                        {batchTests.map((test) => {
                          const score = testScores[test.id];
                          return (
                            <div 
                              key={test.id} 
                              className='flex justify-center'
                              title={`${test.title}: ${score ? `${score.percentage.toFixed(1)}%` : 'Not taken'}`}
                            >
                              <div className={`w-12 h-10 flex items-center justify-center rounded text-xs font-medium ${
                                score ? getScoreColor(score.percentage) : 'bg-gray-100 text-gray-400'
                              }`}>
                                {score ? `${score.percentage.toFixed(0)}%` : '-'}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Average */}
                        <div className='text-center'>
                          <div className='text-xl font-bold text-gray-900'>
                            {student.average_percentage.toFixed(1)}%
                          </div>
                          <div className='text-xs text-gray-500'>
                            {student.tests_completed} tests
                          </div>
                        </div>

                        {/* Details chevron */}
                        <div className='flex justify-center'>
                          {isExpanded ? (
                            <ChevronUp className='w-5 h-5 text-gray-400' />
                          ) : (
                            <ChevronDown className='w-5 h-5 text-gray-400' />
                          )}
                        </div>
                      </div>

                      {/* Mobile Layout */}
                      <div
                        className={`lg:hidden flex items-center p-4 rounded-lg border-2 transition-all hover:shadow-md cursor-pointer ${
                          isTopThree ? 'bg-gradient-to-r from-gray-50 to-white ' + getRankBadgeColor(rank) : 'bg-white border-gray-200'
                        }`}
                        onClick={() => batchTests.length > 0 && toggleRowExpand(student.user_id)}
                      >
                        <div className='flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 mr-4 flex-shrink-0'>
                          {getRankIcon(rank)}
                        </div>
                        
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <h3 className='text-base font-semibold text-gray-900 truncate'>{student.user_name}</h3>
                            {isTopThree && (
                              <Badge className={getRankBadgeColor(rank)} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                #{rank}
                              </Badge>
                            )}
                          </div>
                          <p className='text-xs text-gray-600 truncate'>{student.user_email}</p>
                        </div>
                        
                        <div className='text-right ml-2'>
                          <div className='text-xl font-bold text-gray-900'>
                            {student.average_percentage.toFixed(1)}%
                          </div>
                          <div className='text-xs text-gray-500'>
                            {student.tests_completed} tests
                          </div>
                        </div>

                        {batchTests.length > 0 && (
                          <div className='ml-2'>
                            {isExpanded ? (
                              <ChevronUp className='w-5 h-5 text-gray-400' />
                            ) : (
                              <ChevronDown className='w-5 h-5 text-gray-400' />
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Expanded Test Details and Topic-wise Performance */}
                      {isExpanded && (batchTests.length > 0 || batchTopics.length > 0) && (
                        <div className='mt-2 ml-4 lg:ml-16 mr-4 space-y-4'>
                          {/* Individual Test Scores */}
                          {batchTests.length > 0 && (
                            <div className='p-4 bg-gray-50 rounded-lg border border-gray-200'>
                              <h4 className='text-sm font-semibold text-gray-700 mb-3'>Individual Test Scores</h4>
                              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'>
                                {batchTests.map((test, idx) => {
                                  const score = testScores[test.id];
                                  return (
                                    <div 
                                      key={test.id}
                                      className={`p-3 rounded-lg border ${score ? 'bg-white' : 'bg-gray-100'}`}
                                    >
                                      <div className='text-xs text-gray-500 mb-1 truncate' title={test.title}>
                                        Test {idx + 1}
                                      </div>
                                      <div className='text-xs text-gray-600 mb-2 truncate' title={test.title}>
                                        {test.title.length > 25 ? test.title.substring(0, 25) + '...' : test.title}
                                      </div>
                                      {score ? (
                                        <div className={`text-lg font-bold ${
                                          score.percentage >= 80 ? 'text-green-600' :
                                          score.percentage >= 60 ? 'text-blue-600' :
                                          score.percentage >= 40 ? 'text-yellow-600' :
                                          'text-red-600'
                                        }`}>
                                          {score.percentage.toFixed(1)}%
                                        </div>
                                      ) : (
                                        <div className='text-lg font-medium text-gray-400'>
                                          Not taken
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Topic-wise Performance */}
                          {batchTopics.length > 0 && student.topic_performance && Object.keys(student.topic_performance).length > 0 && (
                            <div className='p-4 bg-emerald-50 rounded-lg border border-emerald-200'>
                              <div className='flex items-center gap-2 mb-3'>
                                <BookOpen className='w-4 h-4 text-emerald-700' />
                                <h4 className='text-sm font-semibold text-emerald-800'>Topic-wise Performance</h4>
                                <span className='text-xs text-emerald-600'>(Performance by Question Bank)</span>
                              </div>
                              
                              {/* Weakest Areas Alert */}
                              {(() => {
                                const weakTopics = getWeakestTopics(student.topic_performance);
                                if (weakTopics.length > 0) {
                                  return (
                                    <div className='mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg'>
                                      <div className='flex items-center gap-2 text-amber-800'>
                                        <AlertTriangle className='w-4 h-4' />
                                        <span className='text-xs font-medium'>Areas needing improvement:</span>
                                        <span className='text-xs'>
                                          {weakTopics.map(t => `${t.name} (${t.percentage.toFixed(0)}%)`).join(', ')}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              
                              <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'>
                                {batchTopics.map((topic) => {
                                  const perf = student.topic_performance[topic.id];
                                  return (
                                    <div 
                                      key={topic.id}
                                      className={`p-3 rounded-lg border ${perf ? getTopicScoreColor(perf.percentage) : 'bg-gray-100 border-gray-200'}`}
                                    >
                                      <div className='text-xs font-medium mb-1 truncate' title={topic.title}>
                                        {topic.title.length > 20 ? topic.title.substring(0, 20) + '...' : topic.title}
                                      </div>
                                      {topic.subject && (
                                        <div className='text-xs opacity-70 mb-1 truncate'>
                                          {topic.subject}
                                        </div>
                                      )}
                                      {perf ? (
                                        <>
                                          <div className='text-lg font-bold'>
                                            {perf.percentage.toFixed(0)}%
                                          </div>
                                          <div className='text-xs opacity-70'>
                                            {perf.correct}/{perf.total} correct
                                          </div>
                                        </>
                                      ) : (
                                        <div className='text-sm font-medium text-gray-400'>
                                          No data
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Performance Legend */}
                              <div className='mt-3 pt-3 border-t border-emerald-200'>
                                <div className='flex flex-wrap gap-3 text-xs'>
                                  <span className='flex items-center gap-1'>
                                    <span className='w-3 h-3 rounded bg-green-500'></span> 80%+ Excellent
                                  </span>
                                  <span className='flex items-center gap-1'>
                                    <span className='w-3 h-3 rounded bg-blue-500'></span> 60-79% Good
                                  </span>
                                  <span className='flex items-center gap-1'>
                                    <span className='w-3 h-3 rounded bg-yellow-500'></span> 40-59% Needs Work
                                  </span>
                                  <span className='flex items-center gap-1'>
                                    <span className='w-3 h-3 rounded bg-red-500'></span> &lt;40% Focus Area
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {rankings.length > 0 && (
              <div className='bg-gray-50 p-4 border-t'>
                <p className='text-sm text-gray-600 text-center'>
                  Rankings based on average percentage. Click on a row to see individual test scores and topic-wise performance analysis.
                </p>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default BatchRankingsNew;
